// src/components/Header.jsx
import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, User, LogOut } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../assets/Revibelogo.png";
import Login from "./Login";
import Register from "./Register";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Load user from localStorage and listen for updates
  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem("userData");
      setUser(storedUser ? JSON.parse(storedUser) : null);
    };
    loadUser();
    window.addEventListener("userUpdated", loadUser);
    window.addEventListener("authStateChanged", loadUser);
    return () => {
      window.removeEventListener("userUpdated", loadUser);
      window.removeEventListener("authStateChanged", loadUser);
    };
  }, []);

  // Listen for openRegisterModal event (fired after OTP verify when profile is incomplete)
  useEffect(() => {
    const handleOpenRegister = () => {
      setIsLoginOpen(false);
      setIsRegisterOpen(true);
    };
    window.addEventListener("openRegisterModal", handleOpenRegister);
    return () => window.removeEventListener("openRegisterModal", handleOpenRegister);
  }, []);

  const handleLogout = () => {
    // Clear all localStorage items
    localStorage.removeItem("userData");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("isProfileCreated");
    localStorage.removeItem("tempUserId");
    
    setUser(null);
    
    // Dispatch events to update all components
    window.dispatchEvent(new Event("userUpdated"));
    window.dispatchEvent(new Event("authStateChanged"));
    
    // Navigate to HOME page on logout
    navigate("/");
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const handleMyProfile = () => {
    setIsProfileDropdownOpen(false);
    navigate("/profile");
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsProfileDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsProfileDropdownOpen(false);
    }, 200);
  };

  const openLogin = () => {
    setIsMenuOpen(false);
    setIsLoginOpen(true);
  };

  // Called from Login modal after successful OTP verify when profile is incomplete
  const handleOpenRegister = () => {
    setIsLoginOpen(false);
    setIsRegisterOpen(true);
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-[#d0d9e5] backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/">
              <img src={logo} alt="Revibe logo" className="h-12" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex gap-8 font-medium text-slate-700">
              {["Home", "About", "Pricing", "Contact"].map((item) => (
                <Link
                  key={item}
                  to={`/${item === "Home" ? "" : item.toLowerCase()}`}
                  className="hover:text-slate-900 transition-colors"
                >
                  {item}
                </Link>
              ))}
            </nav>

            {/* Desktop Right */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/pricing"
                className="px-5 py-2 bg-[#BBE520] text-[#1a2e00] font-medium rounded-lg hover:brightness-95 transition-all"
              >
                Get Started
              </Link>

              {user ? (
                <div
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-[#BBE520]/30 flex items-center justify-center text-[#1a2e00] text-xs font-bold flex-shrink-0">
                      {getInitials(user.firstName, user.lastName)}
                    </div>
                    <span className="text-sm font-medium text-slate-800">
                      {user.firstName} {user.lastName}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-500 transition-transform duration-200 ${
                        isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Dropdown */}
                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 origin-top-right">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-xs text-slate-500 mb-1">Email</p>
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={handleMyProfile}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <User size={16} className="text-slate-500" />
                        My Profile
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-slate-100 mt-1"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={openLogin}
                  className="px-5 py-2 border border-slate-400 rounded-lg text-slate-700 hover:bg-white/50 transition-colors"
                >
                  Login
                </button>
              )}
            </div>

            {/* Mobile Toggle */}
            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white px-6 py-5 space-y-5 shadow-lg border-t border-slate-200">
            {["Home", "About", "Pricing", "Contact"].map((item) => (
              <Link
                key={item}
                to={`/${item === "Home" ? "" : item.toLowerCase()}`}
                onClick={() => setIsMenuOpen(false)}
                className="block text-base font-medium text-slate-700 hover:text-slate-900"
              >
                {item}
              </Link>
            ))}

            <Link
              to="/pricing"
              onClick={() => setIsMenuOpen(false)}
              className="block text-center px-5 py-2 bg-[#BBE520] text-[#1a2e00] font-medium rounded-lg"
            >
              Get Started
            </Link>

            {user ? (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#BBE520]/30 flex items-center justify-center text-[#1a2e00] text-sm font-bold flex-shrink-0">
                    {getInitials(user.firstName, user.lastName)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate("/profile");
                  }}
                  className="w-full py-2 text-sm font-medium bg-[#BBE520]/20 text-[#1a2e00] rounded-lg hover:bg-[#BBE520]/30 transition-colors"
                >
                  My Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={openLogin}
                className="w-full text-center border border-slate-400 py-2 rounded-lg text-slate-700"
              >
                Login
              </button>
            )}
          </div>
        )}
      </header>

      {/* Auth Modals */}
      <Login
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onOpenRegister={handleOpenRegister}
      />
      <Register
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
      />
    </>
  );
};

export default Header;